import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { pseoAPI, articlesAPI } from '../utils/api';
import { ArticleCard } from '../components/ArticleCard';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SEO } from '../components/SEO';
import { FAQSection } from '../components/FAQSection';
import { Newsletter } from '../components/Newsletter';
import { 
  Tag, 
  TrendingUp, 
  ArrowRight, 
  ShoppingBag,
  Percent,
  AlertCircle
} from 'lucide-react';

/**
 * Brand Deals Page - Dynamic page for each brand
 * URL: /brands/:slug
 * Data: Fetched from database via API
 */
export const BrandDealsPage = () => {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [articles, setArticles] = useState([]);
  const [relatedBrands, setRelatedBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchBrandData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch brand from database
        const brandResponse = await pseoAPI.getBrand(slug);
        setBrand(brandResponse.data);
        
        // Fetch related articles (by brand name/tag)
        const articlesResponse = await articlesAPI.getByBrand(slug, undefined, 12);
        setArticles(articlesResponse.data || []);
        
        // Fetch related brands
        if (brandResponse.data?.related_brands?.length > 0) {
          const relatedPromises = brandResponse.data.related_brands.slice(0, 4).map(async (brandSlug) => {
            try {
              const res = await pseoAPI.getBrand(brandSlug);
              return res.data;
            } catch {
              return null;
            }
          });
          const related = await Promise.all(relatedPromises);
          setRelatedBrands(related.filter(Boolean));
        }
      } catch (err) {
        console.error('Failed to fetch brand:', err);
        if (err.response?.status === 404) {
          setError('Brand not found');
        } else {
          setError('Failed to load brand data');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchBrandData();
    }
  }, [slug]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="brand-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">Loading brand deals...</p>
        </div>
      </div>
    );
  }

  // 404 Error state
  if (error === 'Brand not found') {
    return (
      <>
        <SEO 
          title="Brand Not Found - iShopGenie"
          description="The brand you're looking for doesn't exist."
        />
        <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="brand-not-found">
          <div className="text-center max-w-md mx-auto px-4">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-3xl font-playfair font-bold text-gray-900 mb-4">Brand Not Found</h1>
            <p className="text-gray-600 mb-8">
              The brand "{slug}" doesn't exist or hasn't been added yet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/brands"
                className="inline-flex items-center justify-center gap-2 bg-brand-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 smooth-transition"
              >
                <ShoppingBag className="w-5 h-5" />
                Browse All Brands
              </Link>
              <Link 
                to="/"
                className="inline-flex items-center justify-center gap-2 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 smooth-transition"
              >
                Go Home
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state (other errors)
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="brand-error">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-brand-red text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Brands', path: '/brands' }
  ];

  return (
    <>
      <SEO 
        title={brand?.meta_title || `Best ${brand?.name} Deals & Offers (${currentYear})`}
        description={brand?.meta_description || `Discover exclusive ${brand?.name} deals, promo codes, and discounts. Save big on your next purchase.`}
        type="website"
      />

      <div className="min-h-screen bg-gray-50" data-testid="brand-deals-page">
        <Breadcrumbs items={breadcrumbItems} currentPage={brand?.name || slug} />

        {/* Hero Section */}
        <div 
          className="relative py-16"
          style={{ 
            background: brand?.banner_image 
              ? `linear-gradient(135deg, ${brand?.color || '#1F2937'}CC, ${brand?.color || '#1F2937'}99), url(${brand.banner_image}) center/cover`
              : `linear-gradient(135deg, ${brand?.color || '#1F2937'}, ${brand?.color || '#374151'})`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Brand Logo/Initial */}
              <div 
                className="w-24 h-24 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-lg"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                {brand?.logo ? (
                  <img src={brand.logo} alt={brand.name} className="w-16 h-16 object-contain" />
                ) : (
                  brand?.name?.charAt(0) || slug?.charAt(0)?.toUpperCase()
                )}
              </div>
              
              <div className="text-white">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-white/20 text-white text-xs font-bold uppercase rounded-full backdrop-blur-sm">
                    {brand?.category || 'Brand'}
                  </span>
                  {brand?.is_popular && (
                    <span className="px-3 py-1 bg-brand-yellow text-gray-900 text-xs font-bold uppercase rounded-full">
                      Popular
                    </span>
                  )}
                </div>
                
                {/* H1: Best [Brand Name] Deals Today (2026) */}
                <h1 className="text-4xl md:text-5xl font-playfair font-bold mb-4">
                  Best {brand?.name || slug} Deals Today ({currentYear})
                </h1>
                
                <p className="text-white/90 text-lg max-w-2xl">
                  {brand?.short_description || brand?.description?.slice(0, 150) || `Find the latest ${brand?.name || slug} deals, promo codes, and exclusive discounts.`}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Brand Description */}
          {brand?.description && (
            <div className="bg-white rounded-2xl p-8 mb-12 border border-gray-100">
              <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">
                About {brand.name}
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700">
                {brand.description}
              </div>
            </div>
          )}

          {/* Deals Section */}
          <section className="mb-12" data-testid="brand-deals-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-playfair font-bold text-gray-900 flex items-center gap-2">
                <Percent className="w-6 h-6 text-brand-red" />
                {brand?.name} Deals & Offers
              </h2>
            </div>
            
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-12 text-center border border-gray-100" data-testid="no-deals-message">
                <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No deals available yet</h3>
                <p className="text-gray-600 mb-6">
                  We're always adding new {brand?.name || slug} deals. Check back soon or browse other brands.
                </p>
                <Link 
                  to="/brands"
                  className="inline-flex items-center gap-2 text-brand-red font-semibold hover:gap-3 smooth-transition"
                >
                  Browse Other Brands <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </section>

          {/* FAQs */}
          {brand?.faqs && brand.faqs.length > 0 && (
            <FAQSection faqs={brand.faqs} title={`${brand.name} FAQs`} />
          )}

          {/* Related Brands */}
          {relatedBrands.length > 0 && (
            <section className="mb-12" data-testid="related-brands-section">
              <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-brand-green" />
                Related Brands
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {relatedBrands.map((relatedBrand) => (
                  <Link
                    key={relatedBrand.slug}
                    to={`/brands/${relatedBrand.slug}`}
                    className="bg-white rounded-xl p-6 text-center hover:shadow-lg smooth-transition border border-gray-100 hover:border-brand-red group"
                    data-testid={`related-brand-${relatedBrand.slug}`}
                  >
                    <div 
                      className="w-14 h-14 rounded-xl mx-auto mb-3 flex items-center justify-center text-white font-bold text-xl group-hover:scale-110 smooth-transition"
                      style={{ backgroundColor: relatedBrand.color || '#374151' }}
                    >
                      {relatedBrand.name?.charAt(0)}
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-brand-red smooth-transition">
                      {relatedBrand.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                      {relatedBrand.short_description || relatedBrand.category}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Newsletter */}
          <Newsletter />
        </div>
      </div>
    </>
  );
};

export default BrandDealsPage;
